/* eslint-disable no-param-reassign */
import type { LiveUpdateMessageKeys, LiveUpdateMessageMap } from '@evidia/shared';
import { produce } from 'immer';
import { v4 as uuidv4 } from 'uuid';
import type { StoreApi, UseBoundStore } from 'zustand';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

type Fn<T extends LiveUpdateMessageKeys> = { key: string; fn: (data: Omit<LiveUpdateMessageMap[T], 'key'>) => void };

interface Props {
	functions: Partial<Record<LiveUpdateMessageKeys, Fn<any>[]>>;
	addFunction: <T extends LiveUpdateMessageKeys>(key: T, fn: Fn<T>) => void;
	removeFunction: <T extends LiveUpdateMessageKeys>(key: T, fnKey: string) => void;
	clientCodes: Record<string, true>;
	addClientCode: (clientCode: string) => void;
	removeClientCode: (clientCode: string) => void;
	hasClientCode: (clientCode: string) => boolean;
	getNewClientCode: () => string;
}

export const useSocketStore: UseBoundStore<StoreApi<Props>> = create(
	immer<Props>((set, get) => ({
		functions: {},
		addFunction: <T extends LiveUpdateMessageKeys>(key: LiveUpdateMessageKeys, fn: Fn<T>) => {
			set(
				produce(state => {
					if (Array.isArray(state.functions[key])) {
						state.functions[key]!.push(fn);
					} else {
						state.functions[key] = [fn];
					}
				}),
			);
		},
		removeFunction: (key: LiveUpdateMessageKeys, fnKey: string) => {
			set(
				produce((state: Props) => {
					if (Array.isArray(state.functions[key])) {
						state.functions[key] = state.functions[key]!.filter(f => f.key !== fnKey);
					}
				}),
			);
		},
		clientCodes: {},
		addClientCode: clientCode => {
			set(
				produce(state => {
					state.clientCodes[clientCode] = true;
				}),
			);
		},
		removeClientCode: clientCode => {
			set(
				produce(state => {
					delete state.clientCodes[clientCode];
				}),
			);
		},
		hasClientCode: clientCode => get().clientCodes[clientCode] === true,
		getNewClientCode: () => {
			const clientCode = uuidv4();
			get().addClientCode(clientCode);
			return clientCode;
		},
	})),
);

export const useHasClientCode = (clientCode: string): boolean =>
	useSocketStore(state => state.clientCodes[clientCode] === true);
